import React, { useMemo } from "react";
import useWindowWidth from "../../../hooks/useWindowWidth";
import { EDGE } from "../core/edgeTokens";
import { Diamond, DIAMOND_D } from "../core/FeedbackMarker";
import Candle from "../candle/Candle";
import { measureText } from "../core/measureText";
import { candleGeometry, priceTicks } from "../candle/candleModel";
import {
  SWING_KINDS,
  evidenceCandles,
  formatPrice,
  isCloseComparison,
  isSwingEvidence,
  sequenceDomain,
  stretchCandles,
} from "./sequenceModel";

// A multi-candle chart for motion Reads: flat candles, one slot per candle,
// numbers under the candles with the neutral Earlier / Later groups, the
// divider, and a price axis that stays hidden until the reveal. Display only;
// nothing here is tapped.
//
//   candles      every candle in the example (the layout is fixed up front, so
//                nothing moves while the sequence builds)
//   shown        how many are on screen (from 1)
//   context      how many leading candles are reference only: drawn muted
//                grey, with the close marked
//   dividerAfter draw the divider between this candle and the next; it also
//                splits the Earlier / Later groups
//   numbers      candle numbers under the candles (Structure hides them)
//   swings       [{ candle, type }]: neutral markers, each drawn once the two
//                candles after its swing have printed (Structure)
//   showPrices   price axis labels and the values on the evidence overlays
//   evidence     the one evidence item on screen, or null. Switching it
//                replaces the overlay (client: "Evidence must drive the chart").
//                A swingBoundary item carries `name`, the swing's name from
//                its comparison ("Higher low").
//
// Columns, left to right: the candles, the evidence lane (progress arrows, the
// Close / extreme callout), then the price axis. Both lanes are always
// reserved, so nothing shifts when the evidence or the prices appear.
const W = 380;
const H_DESKTOP = 300;
const H_MOBILE = 290;
const AXIS = 40; // price labels
const LANE = 76; // evidence lane
const BOTTOM = 44; // candle numbers, Context and the Earlier / Later groups
const BODY_HALF = 0.3;
// Context candle: grey, so it never reads as price action, but strong enough
// that its body, wick and marked close stay legible (client).
const MUTED = 0.85;
const DIMMED = 0.3;

export default function SequenceChart({
  candles,
  shown,
  context = 0,
  dividerAfter,
  numbers = true,
  swings = [],
  showPrices,
  evidence,
  description,
}) {
  const mobile = useWindowWidth() < 768;
  const H = mobile ? H_MOBILE : H_DESKTOP;
  const plot = { x0: 6, x1: W - AXIS - LANE, y0: 12, y1: H - BOTTOM };
  const lane = { x0: plot.x1, x1: W - AXIS };
  const n = candles.length;

  const yDomain = useMemo(() => sequenceDomain(candles), [candles]);
  // Fewer ticks on a phone (client).
  const ticks = useMemo(() => priceTicks(yDomain, mobile ? 4 : 6), [yDomain, mobile]);
  const tickDecimals = ticks.length > 1 && ticks[1] - ticks[0] < 1 ? 2 : 0;
  const scale = useMemo(() => {
    const [xa, xb] = [0.45, n + 0.55];
    const xs = (plot.x1 - plot.x0) / (xb - xa);
    const ys = (plot.y1 - plot.y0) / (yDomain[1] - yDomain[0]);
    return { x: (i) => plot.x0 + (i - xa) * xs, y: (p) => plot.y1 - (p - yDomain[0]) * ys, unit: xs };
    // plot is derived from H only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [n, H, yDomain]);

  // Swing evidence is read against the whole path, so nothing is dimmed for it.
  const spot = evidence && !isSwingEvidence(evidence) ? new Set(evidenceCandles(evidence)) : null;
  const half = BODY_HALF * scale.unit;
  const groups =
    context && dividerAfter && dividerAfter < n
      ? [
          { label: "Earlier", from: context + 1, to: dividerAfter },
          { label: "Later", from: dividerAfter + 1, to: n },
        ]
      : [];

  return (
    <div role="img" aria-label={description}>
      <svg viewBox={`0 0 ${W} ${H}`} className="block w-full h-auto select-none" aria-hidden="true">
        <rect x={0} y={0} width={W} height={H} fill="#fff" />

        {showPrices &&
          ticks.map((t) => (
            <text key={t} x={W - 3} y={scale.y(t) + 3.5} textAnchor="end" fontSize={10} fill={EDGE.neutral} className="edge-fade-in">
              {t.toFixed(tickDecimals)}
            </text>
          ))}

        {dividerAfter && dividerAfter < n && (
          <line
            x1={scale.x(dividerAfter + 0.5)}
            x2={scale.x(dividerAfter + 0.5)}
            y1={plot.y0}
            y2={plot.y1}
            stroke={EDGE.line}
            strokeWidth={1.2}
            strokeDasharray="3 4"
          />
        )}

        {evidence && <Tints e={evidence} candles={candles} scale={scale} />}

        {candles.slice(0, shown).map((c, i) => {
          const k = i + 1;
          const isContext = k <= context;
          const opacity = isContext ? MUTED : spot && !spot.has(k) ? DIMMED : 1;
          const cx = scale.x(k);
          return (
            <g key={k} className="edge-fade-in">
              <g opacity={opacity}>
                <Candle ohlc={c} index={k} scale={scale} bodyHalf={BODY_HALF} color={isContext ? EDGE.muted : undefined} plain />
              </g>
              {isContext && (
                // The context candle's close stays clearly visible: the
                // sequence is measured from it.
                <line
                  x1={cx - half - 4}
                  x2={cx + half + 0.25 * scale.unit}
                  y1={scale.y(c.close)}
                  y2={scale.y(c.close)}
                  stroke={EDGE.ink}
                  strokeWidth={2.2}
                  shapeRendering="crispEdges"
                />
              )}
            </g>
          );
        })}

        {swings
          .filter((s) => s.candle + 2 <= shown)
          .map((s) => (
            <SwingMarker key={`s-${s.candle}`} x={scale.x(s.candle)} y={scale.y(candles[s.candle - 1][s.type])} high={s.type === "high"} />
          ))}

        {numbers &&
          candles.slice(0, shown).map((c, i) => {
            const k = i + 1;
            const strong = spot && spot.has(k);
            return (
              <text
                key={`n-${k}`}
                x={scale.x(k)}
                y={plot.y1 + 14}
                textAnchor="middle"
                fontSize={10}
                fontWeight={strong ? 700 : 400}
                fill={strong ? EDGE.ink : EDGE.neutral}
              >
                {k}
              </text>
            );
          })}
        {context > 0 && shown >= 1 && (
          <text x={scale.x(1)} y={plot.y1 + 31} textAnchor="middle" fontSize={9} fill={EDGE.neutral}>
            Context
          </text>
        )}
        {/* Earlier / Later stay visible from the start, before Submit (client). */}
        {groups.map((g) => {
          const x1 = scale.x(g.from) - 0.4 * scale.unit;
          const x2 = scale.x(g.to) + 0.4 * scale.unit;
          return (
            <g key={g.label}>
              <line x1={x1} x2={x2} y1={plot.y1 + 20} y2={plot.y1 + 20} stroke={EDGE.line} strokeWidth={1.2} />
              <text x={(x1 + x2) / 2} y={plot.y1 + 32} textAnchor="middle" fontSize={9.5} fill={EDGE.neutral}>
                {g.label}
              </text>
            </g>
          );
        })}

        {evidence && <EvidenceOverlay e={evidence} scale={scale} plot={plot} lane={lane} half={half} candles={candles} showValues={showPrices} />}
      </svg>
    </div>
  );
}

// A soft gold tint behind the candles an item is about (no outline around a
// body or wick). A progress comparison is carried by its arrows instead.
function Tints({ e, candles, scale }) {
  let ranges = [];
  if (e.type === "candle") ranges = [[e.candle, e.candle]];
  else if (e.type === "relation") ranges = [[e.from, e.to]];
  else if (e.type === "swingComparison") ranges = [[e.from, e.from], [e.to, e.to]];
  else if (e.type === "swingBoundary") ranges = [[e.from, e.to]];
  else if (!isCloseComparison(e)) {
    ranges = ["earlier", "later"].map((side) => {
      const ks = stretchCandles(e, side);
      return [ks[0], ks[ks.length - 1]];
    });
  }
  return (
    <g className="edge-fade-in">
      {ranges.map(([a, b]) => {
        const slice = candles.slice(a - 1, b);
        const hi = scale.y(Math.max(...slice.map((c) => c.high))) - 8;
        const lo = scale.y(Math.min(...slice.map((c) => c.low))) + 8;
        const x1 = scale.x(a) - 0.46 * scale.unit;
        const x2 = scale.x(b) + 0.46 * scale.unit;
        return <rect key={`${a}-${b}`} x={x1} y={hi} width={x2 - x1} height={lo - hi} rx={4} fill={EDGE.gold} fillOpacity={0.18} />;
      })}
    </g>
  );
}

function EvidenceOverlay({ e, scale, plot, lane, half, candles, showValues }) {
  const close = (k) => candles[k - 1].close;
  if (isCloseComparison(e)) {
    return <ProgressArrows e={e} scale={scale} lane={lane} half={half} close={close} showChange={showValues} />;
  }
  if (e.type === "swingComparison") return <SwingComparison e={e} scale={scale} plot={plot} lane={lane} candles={candles} showValues={showValues} />;
  if (e.type === "swingBoundary") return <SwingBoundary e={e} scale={scale} plot={plot} lane={lane} half={half} candles={candles} showValues={showValues} />;
  if (e.kind !== "extension") return null;

  // The boundary is always the immediately preceding candle's high or low,
  // named by that candle so a move of the line between items explains itself
  // ("Candle 6 low · 669.43").
  const prevK = e.candle - 1;
  const prev = candles[prevK - 1];
  const cur = candles[e.candle - 1];
  const level = prev[e.boundary];
  const y = scale.y(level);
  const high = e.boundary === "high";
  const extended = high ? cur.high > level : cur.low < level;
  const label = `Candle ${prevK} ${e.boundary}${showValues ? ` · ${formatPrice(level)}` : ""}`;
  const cx = scale.x(e.candle);
  return (
    <g className="edge-fade-in">
      <line
        x1={scale.x(prevK) - half - 6}
        x2={lane.x0 + 2}
        y1={y}
        y2={y}
        stroke={EDGE.gold}
        strokeWidth={2}
        strokeDasharray="5 3"
      />
      {/* Label at the left edge, on the side away from the earlier candles. */}
      <text
        x={plot.x0}
        y={high ? y - 5 : y + 12}
        fontSize={9.5}
        fontWeight={600}
        fill={EDGE.ink}
        stroke="#fff"
        strokeWidth={3}
        paintOrder="stroke"
      >
        {label}
      </text>
      {extended ? (
        // Traded beyond the boundary (closing beyond or back inside it): the
        // exact close, marked with the Phase 1 diamond beside the body and a
        // horizontal leader to a "Close" callout.
        <Callout fromX={cx + half + 2 * DIAMOND_D} y={scale.y(cur.close)} lane={lane} title="Close" value={showValues ? formatPrice(cur.close) : null}>
          <Diamond cx={cx + half} cy={scale.y(cur.close)} side="right" fill={EDGE.gold} />
        </Callout>
      ) : (
        // Did not extend: the candle's own high or low is highlighted, with no
        // Close diamond.
        <Callout fromX={cx + 5} y={scale.y(cur[e.boundary])} lane={lane} title={high ? "High" : "Low"} value={showValues ? formatPrice(cur[e.boundary]) : null}>
          <circle cx={cx} cy={scale.y(cur[e.boundary])} r={4} fill={EDGE.gold} stroke="#fff" strokeWidth={1.5} />
        </Callout>
      )}
    </g>
  );
}

// A neutral swing marker: a small grey triangle just beyond the swing's high
// (pointing down at it) or low (pointing up at it).
const MARK_GAP = 4;
const MARK_SIZE = 6;
function SwingMarker({ x, y, high }) {
  const tip = high ? y - MARK_GAP : y + MARK_GAP;
  const base = high ? tip - MARK_SIZE : tip + MARK_SIZE;
  return <path className="edge-fade-in" d={`M${x - 4} ${base} L${x + 4} ${base} L${x} ${tip} Z`} fill={EDGE.muted} />;
}

// Text beside a swing, past its marker, centred on the candle but kept inside
// the chart.
function SwingLabel({ x, y, high, plot, lane, children }) {
  const w = measureText(children, 9.5, 600);
  const cx = Math.min(Math.max(x, plot.x0 + w / 2), lane.x1 - w / 2);
  return (
    <text
      x={cx}
      y={high ? y - MARK_GAP - MARK_SIZE - 4 : y + MARK_GAP + MARK_SIZE + 12}
      textAnchor="middle"
      fontSize={9.5}
      fontWeight={600}
      fill={EDGE.ink}
      stroke="#fff"
      strokeWidth={3}
      paintOrder="stroke"
    >
      {children}
    </text>
  );
}

// Two swings of one type compared: both marked in gold and joined, the later
// one named ("Higher high · 608.53") and the earlier one priced.
function SwingComparison({ e, scale, plot, lane, candles, showValues }) {
  const { type, name } = SWING_KINDS[e.kind];
  const high = type === "high";
  const [a, b] = [e.from, e.to].map((k) => ({ x: scale.x(k), y: scale.y(candles[k - 1][type]), price: candles[k - 1][type] }));
  return (
    <g className="edge-fade-in">
      <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={EDGE.gold} strokeWidth={2} strokeDasharray="5 3" />
      {[a, b].map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r={4} fill={EDGE.gold} stroke="#fff" strokeWidth={1.5} />
      ))}
      {showValues && (
        <SwingLabel x={a.x} y={a.y} high={high} plot={plot} lane={lane}>
          {formatPrice(a.price)}
        </SwingLabel>
      )}
      <SwingLabel x={b.x} y={b.y} high={high} plot={plot} lane={lane}>
        {`${name}${showValues ? ` · ${formatPrice(b.price)}` : ""}`}
      </SwingLabel>
    </g>
  );
}

// The final move against the structure, read against a marked swing (client):
// the swing's price as the gold dashed line, named as its comparison names it
// ("Higher low · 603.92"), and the final move's lowest low (or highest high)
// marked with the gold circle ("Low · 603.10"). No Close diamond.
function SwingBoundary({ e, scale, plot, lane, half, candles, showValues }) {
  const high = e.boundary === "high";
  const level = candles[e.swing - 1][e.boundary];
  const y = scale.y(level);
  let ext = e.from;
  for (let k = e.from; k <= e.to; k += 1) {
    const v = candles[k - 1][e.boundary];
    if (high ? v > candles[ext - 1].high : v < candles[ext - 1].low) ext = k;
  }
  const extPrice = candles[ext - 1][e.boundary];
  const cx = scale.x(ext);
  const cy = scale.y(extPrice);
  const lineX = Math.max(plot.x0, scale.x(e.swing) - half - 6);
  return (
    <g className="edge-fade-in">
      <line x1={lineX} x2={lane.x0 + 2} y1={y} y2={y} stroke={EDGE.gold} strokeWidth={2} strokeDasharray="5 3" />
      {/* On the side of the line away from the move. */}
      <text
        x={lineX}
        y={high ? y - 5 : y + 12}
        fontSize={9.5}
        fontWeight={600}
        fill={EDGE.ink}
        stroke="#fff"
        strokeWidth={3}
        paintOrder="stroke"
      >
        {`${e.name}${showValues ? ` · ${formatPrice(level)}` : ""}`}
      </text>
      <circle cx={cx} cy={cy} r={4} fill={EDGE.gold} stroke="#fff" strokeWidth={1.5} />
      <text
        x={cx + 8}
        y={cy + 3.5}
        fontSize={9.5}
        fontWeight={600}
        fill={EDGE.ink}
        stroke="#fff"
        strokeWidth={3}
        paintOrder="stroke"
      >
        {`${high ? "High" : "Low"}${showValues ? ` · ${formatPrice(extPrice)}` : ""}`}
      </text>
    </g>
  );
}

// Phase 1 callout geometry: a white label chip with a faint grey outline,
// joined by a dashed connector. The connector is horizontal at the exact price;
// the chip may sit a little above or below so it stays inside the chart, but
// always spans that price.
function Callout({ fromX, y, lane, title, value, children }) {
  const w = lane.x1 - lane.x0 - 10;
  const h = value ? 24 : 15;
  const x = lane.x0 + 8;
  const top = Math.max(2, y - h / 2);
  return (
    <g>
      <line x1={fromX} x2={x} y1={y} y2={y} stroke={EDGE.line} strokeWidth={1.2} strokeDasharray="2 2" />
      {children}
      <rect x={x} y={top} width={w} height={h} rx={6} fill="#fff" stroke={EDGE.line} strokeWidth={1.2} />
      <text x={x + 6} y={top + 10.5} fontSize={9.5} fontWeight={700} fill={EDGE.ink}>
        {title}
      </text>
      {value && (
        <text x={x + 6} y={top + 20} fontSize={9} fill={EDGE.neutral}>
          {value}
        </text>
      )}
    </g>
  );
}

// Close-to-close progress as two arrows side by side in the evidence lane
// (earlier left, later right). Both use the chart's price scale, so their
// lengths are proportional to the moves. Faint gold guides run from each close
// the arrows are measured between to the lane. After the reveal each arrow
// carries its price change, from the exact closes, rounded for display.
function ProgressArrows({ e, scale, lane, half, close, showChange }) {
  const xs = [lane.x0 + 20, lane.x0 + 54];
  const stretches = [e.earlier, e.later];
  const guides = new Map();
  stretches.forEach((s) => {
    [s.fromClose, s.toClose].forEach((k) => guides.set(k, scale.x(k) + half));
  });
  return (
    <g className="edge-fade-in">
      {[...guides].map(([k, x]) => (
        <line
          key={`g-${k}`}
          x1={x}
          x2={xs[1] + 6}
          y1={scale.y(close(k))}
          y2={scale.y(close(k))}
          stroke={EDGE.gold}
          strokeOpacity={0.7}
          strokeWidth={1.2}
          strokeDasharray="2 3"
        />
      ))}
      {stretches.map((s, i) => (
        <Arrow
          key={i}
          x={xs[i]}
          from={close(s.fromClose)}
          to={close(s.toClose)}
          scale={scale}
          label={showChange ? priceChange(close(s.fromClose), close(s.toClose)) : null}
        />
      ))}
    </g>
  );
}

function Arrow({ x, from, to, scale, label }) {
  const ya = scale.y(from);
  const yb = scale.y(to);
  const dir = yb < ya ? -1 : 1; // -1 up, 1 down
  const head = Math.abs(yb - ya) > 8 ? 5 : 0;
  // The change sits just past the arrow head.
  const ly = dir < 0 ? yb - 6 : yb + 13;
  return (
    <g>
      <line x1={x} x2={x} y1={ya} y2={yb - dir * head * 0.6} stroke={EDGE.gold} strokeWidth={2.6} />
      {head > 0 && <path d={`M${x - head} ${yb - dir * head} L${x} ${yb} L${x + head} ${yb - dir * head} Z`} fill={EDGE.gold} />}
      {label && (
        <text
          x={x}
          y={ly}
          textAnchor="middle"
          fontSize={9.5}
          fontWeight={600}
          fill={EDGE.ink}
          stroke="#fff"
          strokeWidth={3}
          paintOrder="stroke"
        >
          {label}
        </text>
      )}
    </g>
  );
}

// "+0.88" / "−10.24": the exact difference of the two closes, rounded to cents
// only for display. The toFixed(6) step clears float noise first, so a true
// 0.875 rounds up to 0.88 and not down.
export function priceChange(from, to) {
  const diff = Number((to - from).toFixed(6));
  const cents = Math.round(Math.abs(diff) * 100) / 100;
  return `${diff < 0 ? "−" : "+"}${cents.toFixed(2)}`;
}

// Screen-reader summary of what is on screen.
export function describeSequence(candles, shown, context) {
  const parts = [`${shown} of ${candles.length} candles shown.`];
  if (context) parts.push(context === 1 ? "Candle 1 is the starting reference." : `Candles 1 to ${context} are the starting reference.`);
  const words = { green: "green", red: "red", doji: "doji", zeroRange: "flat" };
  parts.push(
    candles
      .slice(context, shown)
      .map((c, i) => `Candle ${context + i + 1} ${words[candleGeometry(c).type]}`)
      .join(", ") + "."
  );
  return parts.join(" ");
}
